import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DBCollection, DBSentence, Table } from '../models/db_models';
import { getById, getByIndex, save } from '../utils/dao';
import { SPEECH_STORE } from '../stores/SpeechStore';

interface VoiceItem {
  voice: SpeechSynthesisVoice;
  enabled: boolean;
}

function VoiceSelection() {
  const { id } = useParams<{ id: string }>();
  const [collection, setCollection] = useState<DBCollection | null>(null);
  const [voiceItems, setVoiceItems] = useState<VoiceItem[]>([]);
  const [sampleSentences, setSampleSentences] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const [speechSpeed, setSpeechSpeed] = useState<number>(0.8);

  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setError('No collection ID provided');
        setLoading(false);
        return;
      }

      try {
        const col = await getById(Table.COLLECTIONS, id) as DBCollection | undefined;
        if (!col) {
          setError('Collection not found');
          setLoading(false);
          return;
        }

        setCollection(col);
        setSpeechSpeed(col.speechSpeed || 0.8);

        // Get available voices for the target language
        const voices = SPEECH_STORE.getVoices(col.targetLanguage);

        // Initialize voice items with enabled state
        // If enabledVoices is empty, enable all voices by default
        const enabledVoiceNames = col.enabledVoices || [];
        const shouldEnableAll = enabledVoiceNames.length === 0;

        const items: VoiceItem[] = voices.map(voice => ({
          voice,
          enabled: shouldEnableAll || enabledVoiceNames.includes(voice.name)
        }));

        setVoiceItems(items);

        // Load sample sentences from the collection
        const sentences = await getByIndex(Table.SENTENCES, 'collectionId', id) as DBSentence[];

        // Get 5 random sentences for voice samples
        const shuffled = [...sentences].sort(() => 0.5 - Math.random());
        const samples = shuffled.slice(0, 5).map(s => s.targetSentence);
        setSampleSentences(samples);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const toggleVoice = (voiceName: string) => {
    setVoiceItems(items =>
      items.map(item =>
        item.voice.name === voiceName
          ? { ...item, enabled: !item.enabled }
          : item
      )
    );
  };

  const enableAllVoices = () => {
    setVoiceItems(items =>
      items.map(item => ({ ...item, enabled: true }))
    );
  };

  const disableAllVoices = () => {
    setVoiceItems(items =>
      items.map(item => ({ ...item, enabled: false }))
    );
  };

  const playSample = (voice: SpeechSynthesisVoice) => {
    if (!('speechSynthesis' in window) || sampleSentences.length === 0) {
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    // Pick a random sample sentence
    const randomSentence = sampleSentences[Math.floor(Math.random() * sampleSentences.length)];

    const utterance = new SpeechSynthesisUtterance(randomSentence);
    utterance.voice = voice;
    utterance.lang = voice.lang;
    utterance.rate = speechSpeed;

    setPlayingVoice(voice.name);

    utterance.onend = () => {
      setPlayingVoice(null);
    };

    utterance.onerror = () => {
      setPlayingVoice(null);
    };

    window.speechSynthesis.speak(utterance);
  };

  const handleSave = async () => {
    if (!collection || !id) return;

    try {
      // Get the names of all enabled voices
      const enabledVoiceNames = voiceItems
        .filter(item => item.enabled)
        .map(item => item.voice.name);

      // Update the collection with the new enabled voices and speech speed
      const updatedCollection = { ...collection, enabledVoices: enabledVoiceNames, speechSpeed };
      await save(updatedCollection);

      // Navigate back to the collection page
      window.history.back();
    } catch (err) {
      alert(`Failed to save voice selection: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  if (loading) {
    return (
      <div className="container text-center mt-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading voices...</span>
        </div>
        <p className="mt-3">Loading voices...</p>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="container text-center mt-5">
        <p className="text-danger">Error: {error || 'Collection not found'}</p>
        <p className="mt-4">
          <Link to={`/collection/${id}`} className="text-primary">← Back to Collection</Link>
        </p>
      </div>
    );
  }

  const enabledCount = voiceItems.filter(item => item.enabled).length;

  return (
    <div className="container py-4">
      <div className="mb-4">
        <h1 className="mb-3">Select Voices</h1>
        <p className="text-muted">
          Collection: {collection.knownLanguage} → {collection.targetLanguage}
        </p>
        <p className="text-info">
          {enabledCount} of {voiceItems.length} voices enabled
        </p>

        <div className="mt-3">
          <label className="form-label fw-bold">Speech Speed:</label>
          <div className="btn-group d-flex" role="group">
            <button
              type="button"
              className={`btn ${speechSpeed === 0.6 ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSpeechSpeed(0.6)}
            >
              Very Slow (60%)
            </button>
            <button
              type="button"
              className={`btn ${speechSpeed === 0.8 ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSpeechSpeed(0.8)}
            >
              Slow (80%)
            </button>
            <button
              type="button"
              className={`btn ${speechSpeed === 1.0 ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSpeechSpeed(1.0)}
            >
              Normal
            </button>
            <button
              type="button"
              className={`btn ${speechSpeed === 1.2 ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSpeechSpeed(1.2)}
            >
              Fast (120%)
            </button>
          </div>
        </div>
      </div>

      {voiceItems.length === 0 && (
        <div className="alert alert-warning">
          No voices available for this language.
        </div>
      )}

      {voiceItems.length > 0 && (
        <div className="d-flex gap-2 mb-3">
          <button
            className="btn btn-outline-success btn-sm"
            onClick={enableAllVoices}
          >
            Enable All Voices
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={disableAllVoices}
          >
            Disable All Voices
          </button>
        </div>
      )}

      <div className="list-group mb-4">
        {voiceItems.map((item) => (
          <div
            key={item.voice.name}
            className={`list-group-item ${item.enabled ? 'list-group-item-success' : ''}`}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="flex-grow-1">
                <h5 className="mb-1">{item.voice.name}</h5>
                <small className="text-muted">
                  {item.voice.lang} | {item.voice.localService ? 'Local' : 'Remote'}
                </small>
              </div>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => playSample(item.voice)}
                  disabled={playingVoice === item.voice.name || sampleSentences.length === 0}
                >
                  {playingVoice === item.voice.name ? 'Playing...' : 'Listen'}
                </button>

                <button
                  className={`btn btn-sm ${item.enabled ? 'btn-success' : 'btn-outline-secondary'}`}
                  onClick={() => toggleVoice(item.voice.name)}
                >
                  {item.enabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex gap-3 justify-content-center">
        <button
          className="btn btn-primary btn-lg"
          onClick={handleSave}
          disabled={enabledCount === 0}
        >
          Save Selection
        </button>

        <Link
          to={`/collection/${id}`}
          className="btn btn-secondary btn-lg"
        >
          Cancel
        </Link>
      </div>

      {enabledCount === 0 && (
        <div className="alert alert-warning mt-3 text-center">
          Please enable at least one voice before saving.
        </div>
      )}
    </div>
  );
}

export default VoiceSelection;
